// Demo search and app status, as ledger.search and ledger.shell_status.
import { demoStore } from './mockDocuments';
import { inboxToCheck } from './mockInbox';
import { allJournals } from './mockLedger';
import { runsAwaitingApproval } from './mockSettings';
import { unmatchedBankLines } from './mockBank';
import type { LedgerSource, SearchResults, ShellStatus } from './source';

type SearchPart = Pick<LedgerSource, 'search' | 'status'>;

const delay = <T,>(v: T, ms = 60): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const DEMO_TODAY = '2026-10-11';

/** "1,230.00", "€1230" → 1230; anything else → null (as ledger._search_amount). */
export function searchAmount(q: string): number | null {
  return /^\s*[€£$]?\s*-?[0-9][0-9,]*(\.[0-9]{1,2})?\s*$/.test(q) ? Number(q.replace(/[\s€£$,]/g, '')) : null;
}

function run(qIn: string, limit = 8): SearchResults {
  const q = qIn.trim();
  const empty: SearchResults = { q, contacts: [], documents: [], journals: [], accounts: [] };
  if (q.length < 2) return empty;
  const has = (v: string | null | undefined) => !!v && v.toLowerCase().includes(q.toLowerCase());
  const amt = searchAmount(q);
  const near = (v: string | number | null | undefined) => amt !== null && v !== null && v !== undefined && Math.abs(Math.abs(Number(v)) - Math.abs(amt)) < 0.005;
  const base = (v: string, rate?: string) => Math.round(Number(v) * Number(rate ?? '1') * 100) / 100;

  const contacts = demoStore.contacts
    .filter((c) => has(c.name) || has(c.email) || (!!c.vat_number && c.vat_number.replace(/\s/g, '').toLowerCase().includes(q.replace(/\s/g, '').toLowerCase())))
    .sort((a, b) => Number(!a.name.toLowerCase().startsWith(q.toLowerCase())) - Number(!b.name.toLowerCase().startsWith(q.toLowerCase())) || a.name.localeCompare(b.name))
    .slice(0, limit)
    .map((c) => ({ id: c.id, name: c.name, is_customer: !!c.is_customer, is_supplier: !!c.is_supplier, country: c.country ?? null, currency: c.currency ?? null, email: c.email ?? null }));

  const documents = demoStore.all()
    .filter((d) => has(d.doc_no) || has(d.supplier_ref) || has(d.reference) || has(d.contact_name)
      || near(d.gross_total) || near(base(d.gross_total, d.fx_rate)) || near(d.outstanding) || near(d.net_total))
    .sort((a, b) => b.issue_date.localeCompare(a.issue_date))
    .slice(0, limit)
    .map((d) => ({ id: d.id, type: d.type, doc_no: d.doc_no ?? null, supplier_ref: d.supplier_ref ?? null, reference: d.reference ?? null,
      contact_name: d.contact_name, issue_date: d.issue_date, status: d.status, currency: d.currency,
      gross_total: Number(d.gross_total), base_gross_total: base(d.gross_total, d.fx_rate) }));

  // Journals not made by a document (those are found as the document)
  const journals = allJournals()
    .filter((j) => !['sales_invoice', 'sales_credit', 'purchase_bill', 'purchase_credit', 'receipt', 'payment'].includes(j.source))
    .filter((j) => has(j.journal_no) || has(j.description) || j.lines.some((l) => near(l.amount)))
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, limit)
    .map((j) => ({ id: j.id, journal_no: j.journal_no, date: j.date, source: j.source, description: j.description }));

  const accounts = amt !== null ? [] : demoStore.accounts
    .filter((a) => a.code.toLowerCase().startsWith(q.toLowerCase()) || has(a.name))
    .slice(0, limit)
    .map((a) => ({ code: a.code, name: a.name, class: a.class, is_active: a.is_active !== false }));

  return { q, amount: amt, contacts, documents, journals, accounts };
}

export const demoSearch: SearchPart = {
  search: (q, limit) => delay(run(q, limit)),
  status: () => delay<ShellStatus>({
    counts: {
      overdue_invoices: demoStore.open().filter((d) => d.type === 'sales_invoice' && (d.due_date ?? d.issue_date) < DEMO_TODAY).length,
      inbox_to_check: inboxToCheck(),
      bank_lines: unmatchedBankLines('9999-12-31'),
      recurring_to_approve: runsAwaitingApproval(),
    },
    fiscal_year: { label: 'FY2026', starts_on: '2026-01-01', ends_on: '2026-12-31', closed: false },
    connections: [{ system: 'certtrack', name: 'CertTrack' }],
  }),
};
